type SentryReact = typeof import('@sentry/react');

const parseSampleRate = (value: string | undefined): number | undefined => {
  if (!value) {
    return undefined;
  }

  const sampleRate = Number(value);

  if (!Number.isFinite(sampleRate)) {
    return undefined;
  }

  return Math.min(Math.max(sampleRate, 0), 1);
};

const isSentryEnabled = () => {
  const enabled = import.meta.env.VITE_SENTRY_ENABLED;

  if (enabled !== undefined) {
    return enabled === 'true';
  }

  return import.meta.env.PROD;
};

export const initSentry = async (): Promise<SentryReact | null> => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn || !isSentryEnabled()) {
    return null;
  }

  let Sentry: SentryReact;

  try {
    Sentry = await import('@sentry/react');
  } catch (error) {
    console.error('Błąd inicjalizacji Sentry:', error);
    return null;
  }

  const tracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
  );
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  );
  const isTracingEnabled =
    tracesSampleRate !== undefined && tracesSampleRate > 0;
  const isReplayEnabled =
    (replaysSessionSampleRate !== undefined &&
      replaysSessionSampleRate > 0) ||
    (replaysOnErrorSampleRate !== undefined && replaysOnErrorSampleRate > 0);

  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      ...(isTracingEnabled ? [Sentry.browserTracingIntegration()] : []),
      ...(isReplayEnabled
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]
        : []),
    ],
    ...(tracesSampleRate !== undefined ? { tracesSampleRate } : {}),
    ...(replaysSessionSampleRate !== undefined
      ? { replaysSessionSampleRate }
      : {}),
    ...(replaysOnErrorSampleRate !== undefined
      ? { replaysOnErrorSampleRate }
      : {}),
  });

  return Sentry;
};
